import { type CalendarDate, parseDate, today } from "@internationalized/date";
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import {
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarHeaderCell,
  type DateValue,
  Heading,
  CalendarGridHeader as RACCalendarGridHeader,
  RangeCalendar,
  type RangeValue,
  useLocale,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import {
  activeExploreDatePreset,
  applyExploreDatePreset,
  EXPLORE_DATE_PRESETS,
  type ExploreFilterState,
  formatExploreDateRangeLabel,
} from "@blis/insights/explore/explore-filter-state";

import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { Popover } from "@/components/ui/popover";
import { focusRing, useMediaQuery } from "@/components/ui/utils";

const cellStyles = tv({
  extend: focusRing,
  base: [
    "relative flex size-8 items-center justify-center rounded-md p-0 text-xs font-normal text-foreground outline-offset-2",
    "data-unavailable:pointer-events-none data-unavailable:line-through data-unavailable:opacity-30",
    "data-hovered:bg-accent data-hovered:text-foreground",
    "data-selected:rounded-none data-selected:bg-primary/12 data-selected:text-foreground",
    "data-selection-start:rounded-md data-selection-start:bg-primary data-selection-start:text-primary-foreground",
    "data-selection-end:rounded-md data-selection-end:bg-primary data-selection-end:text-primary-foreground",
  ],
  variants: {
    isDisabled: { true: "pointer-events-none opacity-30" },
    isToday: {
      true: "after:pointer-events-none after:absolute after:bottom-0.5 after:start-1/2 after:z-10 after:size-[3px] after:-translate-x-1/2 after:rounded-full after:bg-primary data-selection-start:after:bg-primary-foreground data-selection-end:after:bg-primary-foreground",
    },
  },
});

function toCalendarRange(
  state: ExploreFilterState,
): RangeValue<CalendarDate> | null {
  if (!state.dateSince || !state.dateUntil) return null;
  try {
    return {
      start: parseDate(state.dateSince),
      end: parseDate(state.dateUntil),
    };
  } catch {
    return null;
  }
}

export function ExploreDateRangeSelector({
  state,
  onChange,
}: {
  state: ExploreFilterState;
  onChange: (next: ExploreFilterState) => void;
}) {
  const [open, setOpen] = useState(false);
  const { direction } = useLocale();
  const isNarrow = useMediaQuery("(max-width: 640px)");
  const maxDate = today("UTC");
  const now = maxDate;

  const activePreset = activeExploreDatePreset(state);
  const calendarValue = useMemo(() => toCalendarRange(state), [state]);

  const monthCount = isNarrow ? 1 : 2;

  function handleRangeChange(range: RangeValue<DateValue>) {
    onChange({
      ...state,
      dateSince: range.start.toString(),
      dateUntil: range.end.toString(),
    });
  }

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Button
        variant="outline"
        size="sm"
        className="h-8 min-w-[10rem] max-w-[16rem] justify-start gap-2 px-2.5 font-normal"
      >
        <CalendarIcon className="size-4 shrink-0 opacity-70" />
        <span className="min-w-0 truncate text-left">
          {formatExploreDateRangeLabel(state)}
        </span>
      </Button>

      <Popover
        className="w-auto max-w-[calc(100vw-2rem)] p-0"
        placement="bottom start"
        respectScreen
      >
        <Dialog
          aria-label="Date range"
          hideCloseButton
          className="outline-hidden"
        >
          <div className="flex max-sm:flex-col">
            <div className="flex shrink-0 flex-col gap-0.5 border-b border-border p-1 sm:w-36 sm:border-b-0 sm:border-r">
              {EXPLORE_DATE_PRESETS.map((preset) => (
                <Button
                  key={preset.id}
                  variant={activePreset === preset.id ? "secondary" : "ghost"}
                  size="sm"
                  className="justify-start"
                  onPress={() =>
                    onChange(applyExploreDatePreset(state, preset.id))
                  }
                >
                  {preset.label}
                </Button>
              ))}
            </div>

            <div className="p-3">
              <RangeCalendar
                aria-label="Custom date range"
                value={calendarValue}
                onChange={handleRangeChange}
                maxValue={maxDate}
                visibleDuration={{ months: monthCount }}
                className="w-full"
              >
                <header className="mb-2 flex items-center gap-1">
                  <Button variant="ghost" size="icon-sm" slot="previous">
                    {direction === "rtl" ? (
                      <ChevronRight aria-hidden className="size-4" />
                    ) : (
                      <ChevronLeft aria-hidden className="size-4" />
                    )}
                  </Button>
                  <Heading className="flex-1 text-center text-sm font-medium" />
                  <Button variant="ghost" size="icon-sm" slot="next">
                    {direction === "rtl" ? (
                      <ChevronLeft aria-hidden className="size-4" />
                    ) : (
                      <ChevronRight aria-hidden className="size-4" />
                    )}
                  </Button>
                </header>
                <div className="flex gap-4">
                  {Array.from({ length: monthCount }, (_, i) => (
                    <CalendarGrid
                      key={i}
                      offset={i > 0 ? { months: i } : undefined}
                    >
                      <RACCalendarGridHeader>
                        {(day) => (
                          <CalendarHeaderCell className="text-[0.65rem] font-medium text-muted-foreground">
                            {day}
                          </CalendarHeaderCell>
                        )}
                      </RACCalendarGridHeader>
                      <CalendarGridBody>
                        {(date) => (
                          <CalendarCell
                            date={date}
                            className={(renderProps) =>
                              cellStyles({
                                ...renderProps,
                                isToday: date.compare(now) === 0,
                              })
                            }
                          />
                        )}
                      </CalendarGridBody>
                    </CalendarGrid>
                  ))}
                </div>
              </RangeCalendar>
            </div>
          </div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}
